/**
 * Bluetooth LE Service for Web Trainer
 * Based on Auuki BLE implementation
 * Supports FTMS devices (power, cadence, heart rate)
 */

class BluetoothService {
  constructor() {
    this.device = null;
    this.server = null;
    this.ftmsService = null;
    this.indoorBikeCharacteristic = null;
    this.controlPointCharacteristic = null;
    this.isConnected = false;
    this.onDataCallback = null;
    this.hasControlPoint = false;
    
    // Bluetooth UUIDs
    this.uuids = {
      fitnessMachine: '00001826-0000-1000-8000-00805f9b34fb',
      indoorBikeData: '00002ad2-0000-1000-8000-00805f9b34fb',
      fitnessMachineControlPoint: '00002ad9-0000-1000-8000-00805f9b34fb',
      heartRate: '0000180d-0000-1000-8000-00805f9b34fb',
      heartRateMeasurement: '00002a37-0000-1000-8000-00805f9b34fb',
      cyclingPower: '00001818-0000-1000-8000-00805f9b34fb',
      cyclingPowerMeasurement: '00002a63-0000-1000-8000-00805f9b34fb'
    };
  }

  // Check if Web Bluetooth is available
  isAvailable() {
    return ('bluetooth' in navigator);
  }

  // Set callback for receiving data
  setDataCallback(callback) {
    this.onDataCallback = callback;
  }

  // Connect to FTMS device
  async connect() {
    try {
      if (!this.isAvailable()) {
        throw new Error('Web Bluetooth is not available in this browser');
      }

      console.log('🔍 Requesting Bluetooth device...');
      
      // Request device with FTMS service
      this.device = await navigator.bluetooth.requestDevice({
        filters: [
          { services: [this.uuids.fitnessMachine] },
          { services: [this.uuids.cyclingPower] }
        ],
        optionalServices: [
          this.uuids.heartRate,
          this.uuids.fitnessMachine,
          this.uuids.cyclingPower
        ]
      });

      console.log('📱 Device selected:', this.device.name);

      // Connect to GATT server
      this.server = await this.device.gatt.connect();
      console.log('🔗 Connected to GATT server');

      // Try to connect to FTMS service first
      await this.connectFTMS();

      this.isConnected = true;
      console.log('✅ Bluetooth connection established');

      // Handle disconnection
      this.device.addEventListener('gattserverdisconnected', () => {
        console.log('📱 Device disconnected');
        this.isConnected = false;
        this.device = null;
        this.server = null;
        this.ftmsService = null;
        this.indoorBikeCharacteristic = null;
        this.controlPointCharacteristic = null;
        this.hasControlPoint = false;
      });

      return true;
    } catch (error) {
      console.error('❌ Bluetooth connection failed:', error);
      throw error;
    }
  }

  // Connect to FTMS service for power/cadence data
  async connectFTMS() {
    try {
      console.log('🚴 Connecting to FTMS service...');
      
      this.ftmsService = await this.server.getPrimaryService(this.uuids.fitnessMachine);
      console.log('📊 FTMS service connected');

      // Get indoor bike data characteristic
      this.indoorBikeCharacteristic = await this.ftmsService.getCharacteristic(this.uuids.indoorBikeData);
      console.log('📈 Indoor bike data characteristic found');

      // Start notifications
      await this.indoorBikeCharacteristic.startNotifications();
      console.log('🔔 Notifications started for indoor bike data');

      // Listen for data
      this.indoorBikeCharacteristic.addEventListener('characteristicvaluechanged', (event) => {
        this.handleFTMSData(event.target.value);
      });

      // Try to get control point characteristic for sending commands
      try {
        this.controlPointCharacteristic = await this.ftmsService.getCharacteristic(this.uuids.fitnessMachineControlPoint);
        console.log('🎮 FTMS Control Point characteristic found');
        
        // Start notifications for control responses
        await this.controlPointCharacteristic.startNotifications();
        console.log('🔔 Notifications started for control point');
        
        // Listen for control responses
        this.controlPointCharacteristic.addEventListener('characteristicvaluechanged', (event) => {
          this.handleControlResponse(event.target.value);
        });
        
        this.hasControlPoint = true;
        
        // Request control from the device
        await this.requestControl();
        
      } catch (controlError) {
        console.log('⚠️ FTMS Control Point not available - read-only device');
        this.hasControlPoint = false;
      }

    } catch (error) {
      console.log('⚠️ FTMS service not available, trying cycling power service...');
      await this.connectCyclingPower();
    }
  }

  // Connect to Cycling Power service as fallback
  async connectCyclingPower() {
    try {
      console.log('⚡ Connecting to Cycling Power service...');
      
      const cpsService = await this.server.getPrimaryService(this.uuids.cyclingPower);
      console.log('📊 Cycling Power service connected');

      const powerCharacteristic = await cpsService.getCharacteristic(this.uuids.cyclingPowerMeasurement);
      console.log('📈 Cycling power measurement characteristic found');

      await powerCharacteristic.startNotifications();
      console.log('🔔 Notifications started for power data');

      powerCharacteristic.addEventListener('characteristicvaluechanged', (event) => {
        this.handleCyclingPowerData(event.target.value);
      });

    } catch (error) {
      console.error('❌ No compatible service found:', error);
      throw new Error('Device does not support FTMS or Cycling Power services');
    }
  }

  // Parse FTMS Indoor Bike Data (based on Auuki implementation)
  handleFTMSData(dataView) {
    try {
      const flags = dataView.getUint16(0, true);
      let offset = 2;
      const data = {};

      // Parse based on flags (simplified version)
      // Speed (if present)
      if ((flags & 0x01) === 0) {
        data.speed = dataView.getUint16(offset, true) * 0.01; // km/h
        offset += 2;
      }

      // Skip average speed if present
      if ((flags & 0x02) !== 0) {
        offset += 2;
      }

      // Cadence (if present)
      if ((flags & 0x04) !== 0) {
        data.cadence = dataView.getUint16(offset, true) * 0.5; // rpm
        offset += 2;
      }

      // Skip average cadence if present
      if ((flags & 0x08) !== 0) {
        offset += 2;
      }

      // Skip distance if present
      if ((flags & 0x10) !== 0) {
        offset += 3;
      }

      // Skip resistance if present
      if ((flags & 0x20) !== 0) {
        offset += 2;
      }

      // Power (if present)
      if ((flags & 0x40) !== 0) {
        data.power = dataView.getUint16(offset, true); // watts
        offset += 2;
      }

      // Skip average power if present
      if ((flags & 0x80) !== 0) {
        offset += 2;
      }

      // Skip energy data if present
      if ((flags & 0x100) !== 0) {
        offset += 5; // 3 fields: total, per hour, per minute
      }

      // Heart rate (if present)
      if ((flags & 0x200) !== 0) {
        data.heartRate = dataView.getUint8(offset); // bpm
        offset += 1;
      }

      console.log('📊 FTMS Data:', data);

      // Send data to callback
      if (this.onDataCallback) {
        this.onDataCallback({
          source: 'ftms',
          ...data
        });
      }

    } catch (error) {
      console.error('❌ Error parsing FTMS data:', error);
    }
  }

  // Parse Cycling Power Measurement (simplified)
  handleCyclingPowerData(dataView) {
    try {
      const flags = dataView.getUint16(0, true);
      let offset = 2;
      const data = {};

      // Instantaneous Power (always present)
      data.power = dataView.getInt16(offset, true); // watts
      offset += 2;

      console.log('⚡ Power Data:', data);

      // Send data to callback
      if (this.onDataCallback) {
        this.onDataCallback({
          source: 'cycling_power',
          ...data
        });
      }

    } catch (error) {
      console.error('❌ Error parsing power data:', error);
    }
  }

  // Disconnect from device
  async disconnect() {
    try {
      if (this.device && this.device.gatt.connected) {
        await this.device.gatt.disconnect();
        console.log('📱 Bluetooth device disconnected');
      }
      this.isConnected = false;
    } catch (error) {
      console.error('❌ Error disconnecting:', error);
    }
  }

  // Get connection status
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      deviceName: this.device?.name || null,
      isAvailable: this.isAvailable(),
      hasControlPoint: this.hasControlPoint
    };
  }

  // Handle control point responses
  handleControlResponse(dataView) {
    try {
      const opCode = dataView.getUint8(0);
      const requestCode = dataView.getUint8(1);
      const resultCode = dataView.getUint8(2);
      
      const requests = {
        0x00: 'Request Control',
        0x01: 'Reset',
        0x04: 'Set Target Resistance',
        0x05: 'Set Target Power',
        0x11: 'Set Simulation Parameters'
      };
      
      const results = {
        0x01: 'Success',
        0x02: 'Not Supported',
        0x03: 'Invalid Parameter',
        0x04: 'Operation Failed',
        0x05: 'Not Permitted'
      };
      
      const requestName = requests[requestCode] || `Unknown (0x${requestCode.toString(16)})`;
      const resultName = results[resultCode] || `Unknown (0x${resultCode.toString(16)})`;
      
      console.log(`🎮 FTMS Control Response: ${requestName} -> ${resultName}`);
      
    } catch (error) {
      console.error('❌ Error parsing control response:', error);
    }
  }

  // Request control from the device
  async requestControl() {
    if (!this.hasControlPoint) {
      console.log('⚠️ No control point available');
      return false;
    }

    try {
      const buffer = new ArrayBuffer(1);
      const view = new DataView(buffer);
      view.setUint8(0, 0x00); // Request Control opcode
      
      await this.controlPointCharacteristic.writeValue(buffer);
      console.log('🎮 Requested control from device');
      return true;
    } catch (error) {
      console.error('❌ Error requesting control:', error);
      return false;
    }
  }

  // Set target power on the device
  async setTargetPower(watts) {
    if (!this.hasControlPoint) {
      console.log('⚠️ Cannot set target power - no control point available');
      return false;
    }

    try {
      // FTMS Power Target command: opcode (0x05) + power (2 bytes, little endian)
      const buffer = new ArrayBuffer(3);
      const view = new DataView(buffer);
      view.setUint8(0, 0x05); // Set Target Power opcode
      view.setUint16(1, Math.max(0, Math.min(65534, watts)), true); // Clamp power to valid range
      
      await this.controlPointCharacteristic.writeValue(buffer);
      console.log(`🎮 Sent target power: ${watts}W`);
      return true;
    } catch (error) {
      console.error('❌ Error setting target power:', error);
      return false;
    }
  }

  // Check if device supports power control
  canControlPower() {
    return this.hasControlPoint;
  }
}

export default BluetoothService;